import { Check } from "lucide-react"
import * as React from "react"

import { FormField } from "@/components/ui/form-field"
import { cn } from "@/lib/utils"

import { PasswordInput } from "./password-input"
import { checkPassword, PASSWORD_MAX_LENGTH, type PasswordRequirement } from "./password-rules"
import { PasswordStrength } from "./password-strength"

/*
   NewPasswordFields — choosing a password, the same way on every screen that asks for one.

   The password with its strength meter and checklist, then the confirmation. Add user, accept
   invite, reset password and change password all render this, so the rules, the wording and the
   moment an error appears cannot drift apart between them.

   It is controlled, and owns no rule of its own: `newPasswordReady` (password-rules) is what a
   screen gates its submit on.

   A mismatch is reported once the confirmation can no longer become the password — the moment it
   stops being a prefix of it, or when the person leaves the field — not on the first keystroke of
   a confirmation that is simply unfinished.
*/

export interface NewPasswordFieldsProps {
  password: string
  onPasswordChange: (value: string) => void
  confirmation: string
  onConfirmationChange: (value: string) => void
  /** "New password" where one is being replaced; "Password" where it is the first. */
  label?: string
  /** Lines the screen adds to the checklist, e.g. "Different from your current password". */
  extraRequirements?: readonly PasswordRequirement[]
  /** The API's refusal, worded as it sent it. Shown on the password field. */
  serverError?: string | null
  inputSize?: "md" | "lg"
  disabled?: boolean
  autoFocus?: boolean
  passwordRef?: React.Ref<HTMLInputElement>
  className?: string
}

export function NewPasswordFields({
  password,
  onPasswordChange,
  confirmation,
  onConfirmationChange,
  label = "New password",
  extraRequirements,
  serverError = null,
  inputSize = "md",
  disabled = false,
  autoFocus = false,
  passwordRef,
  className,
}: NewPasswordFieldsProps): JSX.Element {
  const strengthId = React.useId()
  const [confirmationLeft, setConfirmationLeft] = React.useState(false)

  const { tooLong } = checkPassword(password)
  const matches = confirmation.length > 0 && confirmation === password
  const mismatch =
    confirmation.length > 0 &&
    confirmation !== password &&
    (confirmationLeft || !password.startsWith(confirmation))

  const passwordError = tooLong
    ? `Keep it to ${String(PASSWORD_MAX_LENGTH)} characters.`
    : (serverError ?? undefined)

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex flex-col gap-2.5">
        <FormField label={label} required error={passwordError}>
          <PasswordInput
            ref={passwordRef}
            autoComplete="new-password"
            inputSize={inputSize}
            value={password}
            onChange={(event) => {
              onPasswordChange(event.currentTarget.value)
            }}
            disabled={disabled}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- opt-in, off by default: the reset page is this one field, and has always opened on it
            autoFocus={autoFocus}
            aria-describedby={strengthId}
          />
        </FormField>
        <PasswordStrength
          id={strengthId}
          password={password}
          {...(extraRequirements ? { extraRequirements } : {})}
        />
      </div>

      <FormField
        label={`Confirm ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
        required
        error={mismatch ? "Type the same password again." : undefined}
      >
        <PasswordInput
          autoComplete="new-password"
          inputSize={inputSize}
          value={confirmation}
          onChange={(event) => {
            onConfirmationChange(event.currentTarget.value)
            setConfirmationLeft(false)
          }}
          onBlur={() => {
            setConfirmationLeft(true)
          }}
          disabled={disabled}
        />
        {matches && (
          <p
            role="status"
            className="flex items-center gap-1.5 text-caption font-medium text-master"
          >
            <Check aria-hidden="true" className="size-3.5 shrink-0" />
            Passwords match
          </p>
        )}
      </FormField>
    </div>
  )
}
